import { sleep } from '../lib/utils';
import { ApplicationStatusModel } from '../serverGeneratedTypings/applicationStatusModel';
import { UpdateAccountNumberInputModel } from '../serverGeneratedTypings/updateAccountNumberInputModel';
import { UpdateAccountNumberResult } from '../serverGeneratedTypings/updateAccountNumberResult';
import { SetPpiRequest } from '../serverGeneratedTypings/setPpiRequest';
import { SetPpiResponse } from '../serverGeneratedTypings/setPpiResponse';
import { SignStatusModel } from '../serverGeneratedTypings/signStatusModel';
import { StartSignResponse } from '../serverGeneratedTypings/startSignResponse';
import { SubmitApplicationFormResponse } from '../serverGeneratedTypings/submitApplicationFormResponse';
import { ApplicationSignStatusModel } from '../serverGeneratedTypings/applicationSignStatusModel';
import { ApplicantIdModel } from '../serverGeneratedTypings/applicantIdModel';
import { ExtendedApplicationOfferViewModel } from '@n2-common/serverGeneratedTypings/extendedApplicationOfferViewModel';
import { ExtendedApplicationOfferForSignViewModel } from '@n2-common/serverGeneratedTypings/extendedApplicationOfferForSignViewModel';
import { SetPinUrlResponse } from '@n2-common/serverGeneratedTypings/setPinUrlResponse';
import { GenerateSblUrlResponse } from '@n2-common/serverGeneratedTypings/generateSblUrlResponse';
import { logErrorToServerside } from './serverSideLogging';
import { SigningFileUploadSection } from '@n2-common/serverGeneratedTypings/signingFileUploadSection';
import { ServiceInvocationError, DurationExeededError, throwIfNotSuccess } from './errors';
import { N2ApplicationStatus } from '@n2-common/serverGeneratedTypings/n2ApplicationStatus';
import { UpdateAccountNumberStatus } from '@n2-common/serverGeneratedTypings/updateAccountNumberStatus';
import { default as fetch } from '@n2-common/lib/fetchProxy';
import { SsnToGpidModel } from '@n2-common/serverGeneratedTypings/ssnToGpidModel';
import { SsnToGpidResponseModel } from '@n2-common/serverGeneratedTypings/ssnToGpidResponseModel';
import { getGaClientid } from '@n2-common/lib/useTracking';

const polloveride = localStorage?.getItem('poll-duration');

const POLL_DURATION = polloveride ? parseInt(polloveride, 10) : 60_000;

export const allDocumentGuidsExistInUploadSections = (uploadedGuids: string[], documents: SigningFileUploadSection[]) => {
    const allDocumentGuids: string[] = [];
    documents.forEach((k) => {
        if (k.UploadedDocuments) {
            k.UploadedDocuments.forEach((inner) => {
                allDocumentGuids.push(inner.DocumentGuid.toLowerCase());
            });
        }
    });

    for (let i = 0; i < uploadedGuids.length; i++) {
        const elm = uploadedGuids[i];
        if (!allDocumentGuids.includes(elm.toLowerCase())) {
            return false;
        }
    }

    return true;
};

export const waitForUploadSucess = async (uploadedGuids: string[], applicationId: string, applicantId: string) => {
    const duration = POLL_DURATION;
    const startTime = Date.now();
    let now = startTime;
    let res: SignStatusModel;
    do {
        res = await getApplicationDocumentStatus(applicationId, applicantId);
        if (allDocumentGuidsExistInUploadSections(uploadedGuids, res.Documents)) {
            return res;
        }
        await sleep(2000);
        now = Date.now();
    } while (now - startTime < duration);
    const message = `Polling getApplicationDocumentStatus for ${duration / 1000} sec did not result in desired state.`;
    throw new DurationExeededError(message, applicationId, applicantId, 'UploadSucess');
};

export async function pollForExpectedApplicationState(
    applicationId: string,
    applicantId: string,
    condition: (applicationStatus: ApplicationStatusModel) => boolean
): Promise<ApplicationStatusModel>;

export async function pollForExpectedApplicationState(
    applicationId: string,
    applicantId: string,
    condition: (applicationStatus: ApplicationStatusModel) => boolean,
    throwIfExeeded: false
): Promise<ApplicationStatusModel | undefined>;

export async function pollForExpectedApplicationState(
    applicationId: string,
    applicantId: string,
    condition: (applicationStatus: ApplicationStatusModel) => boolean,
    throwIfExeeded: true
): Promise<ApplicationStatusModel>;

/** Polls for a specific application state */
export async function pollForExpectedApplicationState(
    applicationId: string,
    applicantId: string,
    /** Function used to check for desired condition */
    condition: (applicationStatus: ApplicationStatusModel) => boolean,
    /** Should function throw a DurationExeededError on timeout. Defaults to true. */
    throwIfExeeded = true
) {
    const duration = POLL_DURATION;
    const startTime = Date.now();
    let controller: AbortController | null = null;
    let now = startTime;
    let res: ApplicationStatusModel | undefined = undefined;
    do {
        controller && controller.abort();
        controller = new AbortController();
        try {
            res = await getApplicationStatus(applicationId, applicantId, controller.signal);
            if (condition(res)) {
                return res;
            }
        } catch (ex) {
            logErrorToServerside(`Error getting status`, applicationId, null, ex);
        }
        await sleep(2000);
        now = Date.now();
    } while (now - startTime < duration);
    const message = `Polling getApplicationStatus for ${duration / 1000} sec did not result in desired state.`;

    if (throwIfExeeded) {
        throw new DurationExeededError(message, applicationId, applicantId, 'ExpectedApplicationState');
    } else {
        return undefined;
    }
}

export const pollForExpectedSignState = async (
    applicationId: string,
    correctStateFunc: (signStatus: ApplicationSignStatusModel) => boolean,
    signal?: AbortSignal
) => {
    const duration = POLL_DURATION;
    const startTime = Date.now();
    let now = startTime;
    let res: ApplicationSignStatusModel;
    do {
        try {
            res = await getApplicationSignStatus(applicationId, signal);
            if (correctStateFunc(res)) {
                return res;
            }
        } catch (ex) {
            logErrorToServerside('Error ', applicationId, undefined, ex);
        }
        await sleep(2000);
        now = Date.now();
    } while (now - startTime < duration);

    const message = `Polling getApplicationSignStatus for ${duration / 1000} sec did not result in desired state.`;
    throw new DurationExeededError(message, applicationId, '', 'ExpectedSignState');
};

export async function postLoanApplication<T>(payload: T, requestUrl: string): Promise<[number, SubmitApplicationFormResponse]> {
    const antiforgeryToken = document.getElementById('root')?.dataset?.antiforgerytoken;
    const visitorId = await getGaClientid();
    const payloadWithGa = visitorId ? { ...payload, GAClientRef: visitorId } : payload;

    const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': antiforgeryToken || '',
        },
        redirect: 'error',
        body: JSON.stringify(payloadWithGa),
    });
    await throwIfNotSuccess(response, 'ApplicationApiService.PostLoanApplication');

    const res = await response.json();
    return [response.status, res as SubmitApplicationFormResponse];
}

export async function submitSsnToGetGpid(payload: SsnToGpidModel): Promise<[number, SsnToGpidResponseModel]> {
    const antiforgeryToken = document.getElementById('root')?.dataset?.antiforgerytoken;
    const requestUrl = '/api/n2/application/customer/ssnToGpid';
    const response = await fetch(requestUrl, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'X-XSRF-TOKEN': antiforgeryToken || '',
        },
        redirect: 'error',
        body: JSON.stringify(payload),
    });
    await throwIfNotSuccess(response, 'ApplicationApiService.SubmitSsnToGetGpid');

    const res = await response.json();
    return [response.status, res as SsnToGpidResponseModel];
}

export async function setPpi(wantsPpi: boolean | null, applicationId: string, customerId: string) {
    const visitorId = await getGaClientid();

    const payloadWithGa: SetPpiRequest = { WantsPpi: wantsPpi, GAClientRef: visitorId };

    const requestUrl = `/api/n2/application/Ppi/${applicationId}/${customerId}`;
    const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        redirect: 'error',
        body: JSON.stringify(payloadWithGa),
    });
    await throwIfNotSuccess(response, 'ApplicationApiService.SetPpi');

    const res = (await response.json()) as SetPpiResponse;
    return res;
}

export async function setAccountNumber(accountNumber: string, applicationId: string, customerId: string) {
    const payload: UpdateAccountNumberInputModel = { AccountNumber: accountNumber };
    const requestUrl = `/api/n2/application/accountNumber/${applicationId}/${customerId}`;
    const response = await fetch(requestUrl, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
        },
        redirect: 'error',
        body: JSON.stringify(payload),
    });
    await throwIfNotSuccess(response, 'ApplicationApiService.SetAccountNumber');
    const res = (await response.json()) as UpdateAccountNumberResult;
    if (res.Status !== UpdateAccountNumberStatus.Success) {
        throw new ServiceInvocationError(
            response.status,
            'setAccountNumber failed',
            applicationId,
            customerId,
            'ApplicationApiService.SetAccountNumber',
            res
        );
    }
    return res;
}

export async function uploadDocument(applicationId: string, customerGuid: string, documentTypeCode: string, data: FormData) {
    const response = await fetch(
        `/api/n2/application/UploadDocument?applicationId=${applicationId}&customerGuid=${customerGuid}&documentTypeCode=${documentTypeCode}`,
        {
            method: 'POST',
            body: data,
        }
    );
    await throwIfNotSuccess(response, 'ApplicationApiService.UploadDocument');
    const res = (await response.json()) as string[];
    return res;
}

export async function removeDocument(applicationId: string, customerGuid: string, documentId: string): Promise<boolean> {
    try {
        const response = await fetch(
            `/api/n2/application/RemoveUploadedDocument?applicationId=${applicationId}&customerGuid=${customerGuid}&documentId=${documentId}`,
            {
                method: 'POST',
            }
        );
        return response.ok;
    } catch (err) {
        logErrorToServerside('removeDocument failed', applicationId, customerGuid, err);
        return false;
    }
}

export async function startSignSwedishLoans(applicationId: string, customerId: string) {
    const requestUrl = `/api/n2/swedishloanapplication/Sign/${applicationId}/${customerId}`;

    const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        redirect: 'error',
        body: JSON.stringify({}),
    });
    await throwIfNotSuccess(response, 'ApplicationApiService.StartSignSwedishLoans');
    const res = (await response.json()) as StartSignResponse;
    return res;
}

export async function startSignFinishLoans(applicationId: string, customerId: string) {
    const requestUrl = `/api/n2/finishloanapplication/Sign/${applicationId}/${customerId}`;

    const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        redirect: 'error',
        body: JSON.stringify({}),
    });
    await throwIfNotSuccess(response, 'ApplicationApiService.StartSignFinishLoans');
    const res = (await response.json()) as StartSignResponse;
    return res;
}

export async function startSbl(applicationId: string) {
    const requestUrl = `/api/n2/Sbl/Url/${applicationId}`;

    const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        redirect: 'error',
        body: JSON.stringify({}),
    });
    await throwIfNotSuccess(response, 'ApplicationApiService.StartSbl');
    const res = (await response.json()) as GenerateSblUrlResponse;
    return res;
}

export async function startSignNorwegianLoans(applicationId: string, customerId: string) {
    const requestUrl = `/api/n2/norwegianloanapplication/Sign/${applicationId}/${customerId}`;

    const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        redirect: 'error',
        body: JSON.stringify({}),
    });
    await throwIfNotSuccess(response, 'ApplicationApiService.StartSignNorwegianLoans');
    return (await response.json()) as StartSignResponse;
}

export async function getApplicationStatus(applicationId: string, applicantId: string, signal?: AbortSignal) {
    const requestUrl = `/api/n2/application/${applicationId}/${applicantId}`;

    const response = await fetch(requestUrl, { method: 'GET', redirect: 'error', signal });
    return (await response.json()) as ApplicationStatusModel;
}

export async function getExtendedOfferStatus(applicationId: string, applicantId: string) {
    const requestUrl = `/api/n2/application/extended/${applicationId}/${applicantId}`;

    const response = await fetch(requestUrl, { method: 'GET', redirect: 'error' });
    await throwIfNotSuccess(response, 'ApplicationApiService.GetExtendedOfferStatus');
    return (await response.json()) as ExtendedApplicationOfferViewModel;
}

export async function getExtendedOfferStatusForSignPage(applicationId: string, applicantId: string) {
    const requestUrl = `/api/n2/application/extended/sign/${applicationId}/${applicantId}`;

    const response = await fetch(requestUrl, { method: 'GET', redirect: 'error' });
    await throwIfNotSuccess(response, 'ApplicationApiService.GetExtendedOfferStatusForSignPage');
    return (await response.json()) as ExtendedApplicationOfferForSignViewModel;
}

export async function SelectOffer(applicationId: string, applicantId: string, offerId: string) {
    const requestUrl = `/api/n2/application/SelectOffer/${applicationId}/${applicantId}?selectedOfferGuid=${offerId}`;

    const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        redirect: 'error',
        body: JSON.stringify({}),
    });
    await throwIfNotSuccess(response, 'ApplicationApiService.SelectOffer');
    const res = (await response.json()) as object;
    return res;
}

export async function getApplicationDocumentStatus(applicationId: string, applicantId: string, signal?: AbortSignal) {
    const requestUrl = `/api/n2/application/DocumentStatus/${applicationId}/${applicantId}`;

    const response = await fetch(requestUrl, { method: 'GET', redirect: 'error', signal });
    await throwIfNotSuccess(response, 'ApplicationApiService.GetApplicationDocumentStatus');

    return (await response.json()) as SignStatusModel;
}

export async function getApplicationSignStatus(applicationId: string, signal?: AbortSignal) {
    const requestUrl = `/api/n2/application/signstatus/${applicationId}`;

    const response = await fetch(requestUrl, { method: 'GET', redirect: 'error', signal });
    await throwIfNotSuccess(response, 'ApplicationApiService.GetApplicationSignStatus');
    return (await response.json()) as ApplicationSignStatusModel;
}

export async function getApplicatId(applicationId: string) {
    const requestUrl = `/api/n2/application/applicantid/${applicationId}`;
    let status = 0;
    try {
        const response = await fetch(requestUrl, { method: 'GET', redirect: 'error' });
        status = response.status;
        if (!response.ok) {
            throw new ServiceInvocationError(
                status,
                'Failed to get applicantId',
                applicationId,
                undefined,
                'ApplicationApiService.GetApplicatId',
                await response.text()
            );
        }
        await throwIfNotSuccess(response, 'ApplicationApiService.GetApplicatId');
        const result = (await response.json()) as ApplicantIdModel;
        if (result.Status == N2ApplicationStatus.NotFound || !result.ApplicantId) {
            throw new ServiceInvocationError(
                status,
                'Failed to get applicantId',
                applicationId,
                undefined,
                'ApplicationApiService.GetApplicatId',
                result
            );
        }
        return result;
    } catch (err) {
        throw new ServiceInvocationError(status, 'Failed to get applicantId', applicationId, undefined, 'ApplicationApiService.GetApplicatId', err);
    }
}

export async function startCreditCardSigning(applicationId: string, applicantId: string, signal?: AbortSignal): Promise<StartSignResponse> {
    const url = `/api/n2/creditcardapplication/sign/${applicationId}/${applicantId}`;
    const response = await fetch(url, { method: 'POST', signal });
    await throwIfNotSuccess(response, 'ApplicationApiService.StartCreditCardSigning');

    const result = (await response.json()) as StartSignResponse;

    if (result.Error) {
        throw new ServiceInvocationError(
            response.status,
            `Signing start failed: ${result.Error}`,
            applicationId,
            applicantId,
            'ApplicationApiService.StartCreditCardSigning',
            result
        );
    }
    if (!result.Url) {
        throw new ServiceInvocationError(
            response.status,
            "startCreditCardSigning did't return an URL",
            applicationId,
            applicantId,
            'ApplicationApiService.StartCreditCardSigning',
            result
        );
    }
    return result;
}

export async function startDepositSigning(applicationId: string, applicantId: string, signal?: AbortSignal) {
    const url = `/api/n2/depositapplication/sign/${applicationId}/${applicantId}`;
    const response = await fetch(url, { method: 'POST', signal });

    await throwIfNotSuccess(response, 'ApplicationApiService.StartDepositSigning');
    const result = (await response.json()) as StartSignResponse;
    if (result.Error) {
        throw new Error('Signing start failed.');
    }
    return result;
}

export async function getSetPinUrl(applicationId: string, applicantId: string, signal?: AbortSignal) {
    try {
        const url = `/api/n2/creditcardapplication/setpinurl/${applicationId}/${applicantId}`;

        const response = await fetch(url, { method: 'GET', signal });
        const result = (await response.json()) as SetPinUrlResponse;
        if (!result.Url) {
            throw new Error('No signurl in response');
        }
        return result;
    } catch (err) {
        return { Error: `${err}`, Url: null };
    }
}
export async function setRandomPin(applicationId: string, signal?: AbortSignal) {
    const url = `/api/n2/creditcardapplication/randompin/${applicationId}`;
    const response = await fetch(url, { method: 'POST', signal });
    await throwIfNotSuccess(response, 'ApplicationApiService.SetRandomPin');

    return;
}
